import { merge } from "lodash";
import { LocalStorage } from "src/services/localStorageService";
import defaultSettings from "src/settings/default.json";
import { Settings } from "src/types/settings";

let _settings: Settings | null = null;
let _settingsPromise: Promise<Settings> | null = null;

export const toAppUrl = (path: string): string => `${import.meta.env.BASE_URL}${path.replace(/^\/+/, "")}`;

export function getTenantDomainOverride() {
    const domain = LocalStorage.getItem<string | null>("domainOverride");
    return domain || "default";
}

async function fetchJson(url: string): Promise<unknown> {
    const res = await fetch(url, { cache: "no-store" });
    if (!res.ok) {
        throw new Error(`HTTP ${res.status}`);
    }
    return res.json();
}

async function doLoad(): Promise<Settings> {
    // Let the fetch error propagate so ConfigLoader can surface the actual cause (HTTP 404,
    // network error, parse failure) instead of the downstream "missing required field" message.
    const fetched = (await fetchJson(toAppUrl("/config.json"))) as Partial<Settings>;
    const merged = merge(structuredClone(defaultSettings), fetched) as Settings;

    // Tenant override: in dev a Vite middleware serves config/<tenant>.json (see vite.config.mts);
    // on staging the same files are uploaded to dist/dev-tenants/. Production deploys don't ship
    // these — the fetch 404s and we no-op. Only feature flags get overlaid: rewriting
    // api.apiGatewayUrl to a different tenant's gateway would break CORS (e.g. staging origin
    // calling production API).
    const override = getTenantDomainOverride();
    if (override && override !== "default") {
        try {
            const overrideData = (await fetchJson(toAppUrl(`/dev-tenants/${override}.json`))) as Partial<Settings>;
            merge(merged.features, overrideData.features);
        } catch (err) {
            // eslint-disable-next-line no-console
            console.warn(`[configService] tenant override "${override}" not loaded:`, err);
        }
    }

    const required: { path: string; value: unknown }[] = [
        { path: "api.apiGatewayUrl", value: merged.api?.apiGatewayUrl },
        { path: "api.authClientId", value: merged.api?.authClientId },
        { path: "api.authClientSecret", value: merged.api?.authClientSecret },
    ];
    const missing = required.filter((r) => !r.value).map((r) => r.path);
    if (missing.length > 0) {
        throw new Error(`[configService] config.json missing required field(s): ${missing.join(", ")}`);
    }

    return merged;
}

export function loadSettings(): Promise<Settings> {
    if (!_settingsPromise) {
        _settingsPromise = doLoad().then((s) => {
            _settings = s;
            return s;
        });
    }
    return _settingsPromise;
}

// Test-only: lets vitest.setup.ts seed the cache without an async fetch. Guarded so it can't
// be called from production code by accident. Sets both the sync cache and the promise so a
// later loadSettings() call resolves immediately to the seeded value instead of re-running
// doLoad() and trying to fetch /config.json from jsdom.
export function setSettingsForTest(s: Settings): void {
    if (import.meta.env.MODE !== "test") {
        throw new Error("[configService] setSettingsForTest may only be called in test environments");
    }
    _settings = s;
    _settingsPromise = Promise.resolve(s);
}

export const getSettings = (): Settings => {
    if (!_settings) {
        throw new Error(
            "[configService] getSettings() called before loadSettings() resolved. " +
                "Ensure <ConfigLoader> wraps the consumer.",
        );
    }
    return _settings;
};

export function getEnv() {
    return getSettings().envName;
}

export function getApiGatewayUrl(): string {
    return getSettings().api.apiGatewayUrl;
}

export function getHomepageUrl(): string {
    return getSettings().api.homepageUrl;
}

type AuthClient = {
    clientId: string;
    clientSecret: string;
};

export function getAuthClient(): AuthClient {
    return {
        clientId: getSettings().api.authClientId,
        clientSecret: getSettings().api.authClientSecret,
    };
}

export function getReduxVersion(): string {
    return "1.0";
}
